<template>
  <div class="app-container">
    <!-- Header avec switch de mode -->
    <header class="app-header">
      <div class="header-content">
        <div class="logo-section">
          <h1 class="app-title">SymbioSensor</h1>
          <span class="app-subtitle">Cohérence Cardiaque</span>
        </div>
        
        <div class="mode-switcher">
          <div class="switch-container">
            <span 
              :class="['mode-label', { active: currentMode === 'expert' }]"
              @click="switchMode('expert')"
            >
              Moniteur Expert
            </span>
            
            <div class="toggle-switch" @click="toggleMode">
              <div class="toggle-track" :class="{ active: currentMode === 'games' }">
                <div class="toggle-thumb" :class="{ active: currentMode === 'games' }">
                  <svg 
                    v-if="currentMode === 'expert'" 
                    width="16" 
                    height="16" 
                    viewBox="0 0 24 24" 
                    fill="none"
                  >
                    <path 
                      d="M9 11H7l3-7 3 7h-2l3 7-3-7z" 
                      stroke="currentColor" 
                      stroke-width="2" 
                      stroke-linecap="round" 
                      stroke-linejoin="round"
                    />
                  </svg>
                  <svg 
                    v-else 
                    width="16" 
                    height="16" 
                    viewBox="0 0 24 24" 
                    fill="none"
                  >
                    <rect 
                      x="6" 
                      y="4" 
                      width="4" 
                      height="16" 
                      rx="2" 
                      stroke="currentColor" 
                      stroke-width="2"
                    />
                    <rect 
                      x="14" 
                      y="4" 
                      width="4" 
                      height="16" 
                      rx="2" 
                      stroke="currentColor" 
                      stroke-width="2"
                    />
                  </svg>
                </div>
              </div>
            </div>
            
            <span 
              :class="['mode-label', { active: currentMode === 'games' }]"
              @click="switchMode('games')"
            >
              Monitorage Jeux
            </span>
          </div>
          
          <div class="mode-description">
            {{ currentModeDescription }}
          </div>
        </div>
      </div>
    </header>

    <!-- Contenu principal -->
    <main class="app-main">
      <!-- Mode Moniteur Expert -->
      <div v-if="currentMode === 'expert'" class="mode-content">
        <MoniteurExpert />
      </div>
      
      <!-- Mode Monitorage Jeux -->
      <div v-else class="mode-content">
        <MonitorageJeux />
      </div>
    </main>
  </div>
</template>

<script>
import MoniteurExpert from './moniteurExpert/MoniteurExpert.vue'
import MonitorageJeux from './jeux/MonitorageJeux.vue' 

export default {
  name: 'App',
  components: {
    MoniteurExpert,
    MonitorageJeux
  },
  data() {
    return {
      currentMode: 'expert' // 'expert' ou 'games'
    }
  },
  computed: {
    currentModeDescription() {
      return this.currentMode === 'expert' 
        ? 'Interface complète avec métriques détaillées'
        : 'Interface optimisée pour les applications de jeux'
    }
  },
  methods: {
    switchMode(mode) {
      this.currentMode = mode
      console.log(`Mode basculé vers: ${mode}`)
    },
    
    toggleMode() {
      this.currentMode = this.currentMode === 'expert' ? 'games' : 'expert'
    }
  },
  
  // Sauvegarde le mode dans localStorage
  watch: {
    currentMode(newMode) {
      localStorage.setItem('symbiosensor-mode', newMode)
    }
  },
  
  // Restaure le mode au démarrage
  mounted() {
    const savedMode = localStorage.getItem('symbiosensor-mode')
    if (savedMode && ['expert', 'games'].includes(savedMode)) {
      this.currentMode = savedMode
    }
  }
}
</script>

<style scoped>
.app-container {
  min-height: 100vh;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
}

/* === HEADER === */
.app-header {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
  padding: 20px 0;
  /* position: fixed; */
  top: 0;
  z-index: 100;
  box-shadow: 0 2px 20px rgba(0, 0, 0, 0.1);
}

.header-content {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 40px;
}

.logo-section {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.app-title {
  font-size: 28px;
  font-weight: 700;
  color: #2c3e50;
  margin: 0;
  background: linear-gradient(135deg, #489181, #2c3e50);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.app-subtitle {
  font-size: 14px;
  color: #7f8c8d;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 1px;
}

/* === MODE SWITCHER === */
.mode-switcher {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.switch-container {
  display: flex;
  align-items: center;
  gap: 15px;
  padding: 8px 16px;
  background: rgba(255, 255, 255, 0.8);
  border-radius: 50px;
  border: 2px solid #e9ecef;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.mode-label {
  font-size: 14px;
  font-weight: 600;
  color: #6c757d;
  cursor: pointer;
  transition: all 0.3s ease;
  padding: 4px 8px;
  border-radius: 20px;
  user-select: none;
}

.mode-label.active {
  color: #489181;
  background: rgba(72, 145, 129, 0.1);
}

.mode-label:hover:not(.active) {
  color: #495057;
  background: rgba(0, 0, 0, 0.05);
}

/* Toggle Switch */
.toggle-switch {
  cursor: pointer;
  padding: 4px;
}

.toggle-track {
  width: 50px;
  height: 24px;
  background: #e9ecef;
  border-radius: 12px;
  position: relative;
  transition: all 0.3s ease;
  border: 2px solid #dee2e6;
}

.toggle-track.active {
  background: #489181;
  border-color: #489181;
}

.toggle-thumb {
  width: 20px;
  height: 20px;
  background: white;
  border-radius: 50%;
  position: absolute;
  top: 50%;
  left: 2px;
  transform: translateY(-50%);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6c757d;
}

.toggle-thumb.active {
  left: 26px;
  color: #489181;
}

.mode-description {
  font-size: 12px;
  color: #6c757d;
  text-align: center;
  font-style: italic;
  max-width: 200px;
}

/* === MAIN CONTENT === */
.app-main {
  padding: 0;
}

.mode-content {
  animation: fadeIn 0.4s ease-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* === PLACEHOLDER JEUX === */
.jeux-placeholder {
  min-height: calc(100vh - 140px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
}

.placeholder-content {
  text-align: center;
  max-width: 500px;
  background: rgba(255, 255, 255, 0.9);
  padding: 60px 40px;
  border-radius: 20px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.placeholder-icon {
  font-size: 80px;
  margin-bottom: 30px;
  animation: bounce 2s infinite;
}

.placeholder-content h2 {
  color: #2c3e50;
  margin: 0 0 15px 0;
  font-size: 32px;
  font-weight: 700;
}

.placeholder-content p {
  color: #7f8c8d;
  margin: 0 0 30px 0;
  font-size: 18px;
  line-height: 1.5;
}

.coming-soon {
  margin-bottom: 40px;
}

.badge {
  display: inline-block;
  padding: 8px 20px;
  background: linear-gradient(135deg, #f39c12, #e67e22);
  color: white;
  border-radius: 25px;
  font-size: 14px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
  box-shadow: 0 4px 12px rgba(243, 156, 18, 0.3);
}

.features-preview {
  text-align: left;
  margin-top: 30px;
  padding: 25px;
  background: rgba(72, 145, 129, 0.05);
  border-radius: 12px;
  border: 1px solid rgba(72, 145, 129, 0.1);
}

.features-preview h3 {
  color: #489181;
  margin: 0 0 15px 0;
  font-size: 16px;
  font-weight: 600;
}

.features-preview ul {
  list-style: none;
  padding: 0;
  margin: 0;
}

.features-preview li {
  color: #555;
  margin-bottom: 8px;
  font-size: 14px;
  line-height: 1.4;
}

@keyframes bounce {
  0%, 20%, 50%, 80%, 100% {
    transform: translateY(0);
  }
  40% {
    transform: translateY(-10px);
  }
  60% {
    transform: translateY(-5px);
  }
}

/* === RESPONSIVE === */
@media (max-width: 768px) {
  .header-content {
    flex-direction: column;
    gap: 20px;
    text-align: center;
  }
  
  .logo-section {
    align-items: center;
  }
  
  .app-title {
    font-size: 24px;
  }
  
  .switch-container {
    gap: 10px;
    padding: 6px 12px;
  }
  
  .mode-label {
    font-size: 12px;
  }
  
  .toggle-track {
    width: 40px;
    height: 20px;
  }
  
  .toggle-thumb {
    width: 16px;
    height: 16px;
  }
  
  .toggle-thumb.active {
    left: 22px;
  }
  
  .placeholder-content {
    padding: 40px 30px;
    margin: 20px;
  }
  
  .placeholder-icon {
    font-size: 60px;
    margin-bottom: 20px;
  }
  
  .placeholder-content h2 {
    font-size: 24px;
  }
  
  .placeholder-content p {
    font-size: 16px;
  }
}

@media (max-width: 480px) {
  .app-header {
    padding: 15px 0;
  }
  
  .header-content {
    padding: 0 15px;
  }
  
  .app-title {
    font-size: 20px;
  }
  
  .app-subtitle {
    font-size: 12px;
  }
  
  .switch-container {
    flex-direction: column;
    gap: 12px;
    padding: 12px;
  }
  
  .placeholder-content {
    padding: 30px 20px;
    margin: 15px;
  }
  
  .features-preview {
    padding: 20px;
  }
}
</style>